import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Box,
  DialogActions,
  IconButton,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import COLORS from '../../../../resources/style/Themes';

const MissingTranslationsModal = ({
  open,
  onClose,
  translations,
  isLoading,
  currentTranslatingLanguage,
  onAdd,
  currentItem,
}) => {
  const [addedTranslations, setAddedTranslations] = useState(new Set());
  const [editableTranslations, setEditableTranslations] = useState({});
  const [isEditing, setIsEditing] = useState({});
  const [submitting, setSubmitting] = useState({});

  useEffect(() => {
    setAddedTranslations(new Set());
  }, [translations]);

  const handleAdd = async (translation) => {
    const supportLinkToAdd = {
      header:
        editableTranslations[translation.languageCode]?.header ||
        translation.translatedText,
      url:
        editableTranslations[translation.languageCode]?.url || currentItem.url,
      is_active: true,
      link_type: currentItem.link_type,
      name: currentItem.name,
      section_type: currentItem.section_type,
      language_code: translation.languageCode,
    };

    try {
      setSubmitting((prev) => ({ ...prev, [translation.languageCode]: true }));
      const success = await onAdd(supportLinkToAdd);
      if (!success) {
        translation.status = 'error';
        translation.error = 'Failed to add translation';
      } else {
        setAddedTranslations(
          (prev) => new Set([...prev, translation.languageCode]),
        );
      }
    } catch (error) {
      translation.status = 'error';
      translation.error = error.message || 'Failed to add translation';
    } finally {
      setSubmitting((prev) => ({ ...prev, [translation.languageCode]: false }));
    }
  };

  const handleTranslationChange = (languageCode, field, newText) => {
    setEditableTranslations((prev) => ({
      ...prev,
      [languageCode]: {
        ...prev[languageCode],
        [field]: newText,
      },
    }));
  };

  const handleBlur = (translation, field) => {
    setIsEditing((prev) => ({
      ...prev,
      [translation.languageCode + '_' + field]: false,
    }));
    if (editableTranslations[translation.languageCode]) {
      if (field === 'header') {
        translation.translatedText =
          editableTranslations[translation.languageCode].header;
      } else if (field === 'url') {
        translation.url = editableTranslations[translation.languageCode].url;
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onExited: () => {
          setAddedTranslations(new Set());
          setEditableTranslations({});
          setIsEditing({});
          setSubmitting({});
        },
      }}
    >
      <DialogTitle
        sx={{ background: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
      >
        Missing Translations for {currentItem?.header || ''}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'primary.contrastText',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography>
              {currentTranslatingLanguage
                ? `Translating to ${currentTranslatingLanguage}...`
                : 'Loading translations...'}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Language</TableCell>
                <TableCell>Language Code</TableCell>
                <TableCell>Translated Header</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {translations.map((translation, index) => (
                <TableRow key={index}>
                  <TableCell>{translation.languageName}</TableCell>
                  <TableCell>{translation.languageCode}</TableCell>
                  <TableCell
                    onClick={() =>
                      setIsEditing((prev) => ({
                        ...prev,
                        [translation.languageCode + '_header']: true,
                      }))
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    {translation.status === 'error' ? (
                      <Box sx={{ color: 'error.main' }}>
                        {translation.error}
                      </Box>
                    ) : isEditing[translation.languageCode + '_header'] ? (
                      <input
                        autoFocus
                        value={
                          editableTranslations[translation.languageCode]
                            ?.header !== undefined
                            ? editableTranslations[translation.languageCode]
                                .header
                            : translation.translatedText
                        }
                        onChange={(e) =>
                          handleTranslationChange(
                            translation.languageCode,
                            'header',
                            e.target.value,
                          )
                        }
                        onBlur={() => handleBlur(translation, 'header')}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleBlur(translation, 'header');
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    ) : editableTranslations[translation.languageCode]
                        ?.header !== undefined ? (
                      editableTranslations[translation.languageCode].header
                    ) : (
                      translation.translatedText
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      setIsEditing((prev) => ({
                        ...prev,
                        [translation.languageCode + '_url']: true,
                      }))
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    {isEditing[translation.languageCode + '_url'] ? (
                      <input
                        autoFocus
                        value={
                          editableTranslations[translation.languageCode]
                            ?.url !== undefined
                            ? editableTranslations[translation.languageCode].url
                            : currentItem.url
                        }
                        onChange={(e) =>
                          handleTranslationChange(
                            translation.languageCode,
                            'url',
                            e.target.value,
                          )
                        }
                        onBlur={() => handleBlur(translation, 'url')}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleBlur(translation, 'url');
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    ) : editableTranslations[translation.languageCode]?.url !==
                      undefined ? (
                      editableTranslations[translation.languageCode].url
                    ) : (
                      currentItem.url
                    )}
                  </TableCell>
                  <TableCell>
                    {translation.status === 'error' ? (
                      <Box sx={{ color: 'error.main' }}>Failed</Box>
                    ) : submitting[translation.languageCode] ? (
                      <CircularProgress size={24} />
                    ) : addedTranslations.has(translation.languageCode) ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'success.main',
                        }}
                      >
                        <CheckCircle sx={{ mr: 1 }} />
                        Added
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => handleAdd(translation)}
                      >
                        Add
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingTranslationsModal;
